<template>
  <ai-chat-trace-step
    v-if="single !== null"
    :key="single.key"
    class="ai-chat-trace"
    :entry="single"
    compact
  />
  <div
    v-else-if="visible"
    class="ai-chat-trace"
  >
    <!-- MBtn emits on Enter itself and the native button clicks on Enter too: prevent keeps it one toggle -->
    <m-btn
      class="_header"
      :clickable="expandable"
      :aria-expanded="expandable ? expanded : undefined"
      light
      hide-border
      xs
      @click="toggle"
      @keydown.enter.prevent
    >
      <span class="_title">{{ title }}</span>
      <m-icon
        v-if="expandable"
        class="_chevron"
        :type="expanded ? 'down' : 'right'"
        size="11"
        aria-hidden="true"
      />
    </m-btn>
    <div
      v-if="expandable && expanded"
      class="_steps"
    >
      <ai-chat-trace-step
        v-for="entry in entries"
        :key="entry.key"
        :entry="entry"
      />
    </div>
  </div>
</template>

<script setup>
import AiChatTraceStep from '@/components/ai-chat/AiChatTraceStep.vue';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { formatDuration, traceLayout } from '@/lib/ai-chat-trace';
import { useI18n } from 'vue-i18n';

const MIN_UNTRACED_WAIT_MS = 10_000;
const TICK_MS = 1000;

const props = defineProps({
  steps: {
    type: Array,
    required: true,
  },
  running: {
    type: Boolean,
    default: false,
  },
  startedAt: {
    type: Number,
    default: null,
  },
  elapsedMs: {
    type: Number,
    default: 0,
  },
});

const { t } = useI18n();

const mountedAt = Date.now();
const startedAt = computed(() => props.startedAt ?? mountedAt);

const now = ref(Date.now());
let ticker = null;
watch(() => props.running, (running) => {
  clearInterval(ticker);
  if (running) {
    now.value = Date.now();
    ticker = setInterval(() => { now.value = Date.now(); }, TICK_MS);
  }
}, { immediate: true });
onBeforeUnmount(() => clearInterval(ticker));

const title = computed(() => {
  if (props.running) {
    return t('aiChat.trace.running', { duration: formatDuration(now.value - startedAt.value, t) });
  }
  return t('aiChat.trace.took', { duration: formatDuration(props.elapsedMs, t) });
});

const entries = computed(() => {
  const { rows, current } = traceLayout(props.steps, { running: props.running });
  return current === null ? rows : [...rows, current];
});

const single = computed(() => (entries.value.length === 1 ? entries.value[0] : null));

const visible = computed(() => props.running || entries.value.length > 0 || props.elapsedMs >= MIN_UNTRACED_WAIT_MS);

const expandable = computed(() => entries.value.length > 0);
const expanded = ref(props.running);
const toggle = () => { expanded.value = !expanded.value; };
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  .ai-chat-trace {
    color: $font-color-secondary;

    ._header {
      display: inline-flex;
      margin-left: -.6rem;
    }

    ._chevron {
      margin-left: .4rem;
    }

    ._steps {
      display: flex;
      flex-direction: column;
      gap: .2rem;
      padding-left: 1.2rem;
      margin: .4rem 0 0 .2rem;
      border-left: 2px solid $border-color;
    }
  }
</style>
